import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Data
import { CITIES_BY_COUNTRY, LATIN_AMERICAN_COUNTRIES } from "utils/latinAmericanCountries";

// Reglas de validación para los campos del formulario
const validationRules = {
  nombre_representante: {
    required: true,
    maxLength: 255,
    messages: {
      required: "El nombre del representante es obligatorio",
      maxLength: "El nombre no puede exceder 255 caracteres",
    }
  },
  numero_identificacion: {
    required: true,
    pattern: /^\d+$/,
    messages: {
      required: "El número de identificación es obligatorio",
      pattern: "Solo se permiten números",
    }
  },
  correo_electronico: {
    required: true,
    pattern: /\S+@\S+\.\S+/,
    messages: {
      required: "El correo electrónico es obligatorio",
      pattern: "El correo electrónico no es válido",
    }
  },
  telefono_contacto: {
    required: true,
    maxLength: 15,
    pattern: /^\d{1,15}$/,
    messages: {
      required: "El teléfono de contacto es obligatorio",
      maxLength: "El teléfono no puede exceder 15 dígitos",
      pattern: "Solo se permiten hasta 15 dígitos",
    }
  },
  region_operacion_pais: {
    required: true,
    messages: {
      required: "Debe seleccionar un país de operación",
    }
  },
  region_operacion_ciudad: {
    required: true,
    messages: {
      required: "Debe seleccionar una ciudad de operación",
    }
  },
};

function GestionMinoristas() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    nombre_representante: "",
    numero_identificacion: "",
    correo_electronico: "",
    telefono_contacto: "",
    region_operacion_pais: "",
    region_operacion_ciudad: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [errores, setErrores] = useState({});

  // Nuevos estados para validación con MDInput
  const [fieldValidation, setFieldValidation] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});
  const [currentAlert, setCurrentAlert] = useState(null);
  const [showValidationAlert, setShowValidationAlert] = useState(false);

  const validateField = useCallback((fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return { isValid: true, message: '' };

    let isValid = true;
    let message = '';

    if (rules.required && !value.toString().trim()) { // Convert to string for trim()
      isValid = false;
      message = rules.messages.required;
    } else if (rules.minLength && value.length < rules.minLength) {
      isValid = false;
      message = rules.messages.minLength;
    } else if (rules.maxLength && value.length > rules.maxLength) {
      isValid = false;
      message = rules.messages.maxLength;
    } else if (rules.pattern && !rules.pattern.test(value)) {
      isValid = false;
      message = rules.messages.pattern;
    }

    return { isValid, message };
  }, []);

  const showValidationMessage = useCallback((field, validation) => {
    if (!validation.isValid) {
      setCurrentAlert({
        type: 'error',
        message: validation.message,
        field
      });
      setShowValidationAlert(true);
    }
  }, []);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    const validation = validateField(field, value);
    setFieldValidation(prev => ({
      ...prev,
      [field]: validation
    }));

    if (fieldTouched[field]) {
      showValidationMessage(field, validation);
    }

    if (errores[field]) {
      setErrores({
        ...errores,
        [field]: null
      });
    }
  };

  const handleFieldBlur = (field) => () => {
    setFieldTouched(prev => ({
      ...prev,
      [field]: true
    }));

    const validation = fieldValidation[field];
    if (validation && !validation.isValid) {
      showValidationMessage(field, validation);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let formIsValid = true;

    for (const fieldName in validationRules) {
      const { isValid, message } = validateField(fieldName, formData[fieldName]);
      if (!isValid) {
        newErrors[fieldName] = message;
        formIsValid = false;
      }
    }
    setErrores(newErrors);
    return formIsValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowAlert(false); // Reset general alert

    const formIsValid = validateForm();

    if (!formIsValid) {
      setError("Por favor, complete todos los campos obligatorios y corrija los errores.");
      setShowAlert(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Aquí iría la lógica para enviar los datos del formulario
      // Por ejemplo, una llamada a un servicio API
      console.log("Datos del Distribuidor Autorizado:", formData);

      // Simulación de éxito
      setTimeout(() => {
        setLoading(false);
        // navigate('/path-to-success-page'); // Redirigir si es necesario
        setCurrentAlert({ type: 'success', message: 'Distribuidor registrado exitosamente.' });
        setShowValidationAlert(true);
        setFormData({
          nombre_representante: "",
          numero_identificacion: "",
          correo_electronico: "",
          telefono_contacto: "",
          region_operacion_pais: "",
          region_operacion_ciudad: "",
        });
        setErrores({});
        setFieldValidation({});
        setFieldTouched({});
      }, 1500);

    } catch (err) {
      setLoading(false);
      setError(err.message || "Error al registrar el distribuidor. Intente de nuevo.");
      setShowAlert(true);
      console.error("Error al registrar el distribuidor:", err);
    }
  };

  const getFieldColor = (field) => {
    if (!fieldTouched[field]) return 'info';
    const validation = fieldValidation[field];
    return validation?.isValid ? 'success' : 'error';
  };

  const getValidationIcon = (field) => {
    if (!fieldTouched[field]) return null;
    const validation = fieldValidation[field];
    return validation?.isValid ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />;
  };

  // Para el selector de país y ciudad
  const availableCities = formData.region_operacion_pais ? CITIES_BY_COUNTRY[formData.region_operacion_pais] || [] : [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container justifyContent="center" spacing={6}>
          <Grid item xs={12} md={10} lg={8} xl={6}> {/* Ajusta el ancho aquí */}
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="primary" // Usar primary para el encabezado del formulario
                borderRadius="lg"
                coloredShadow="primary" // Usar primary para la sombra
              >
                <MDTypography variant="h6" color="white">
                  Registro de Distribuidor Autorizado
                </MDTypography>
              </MDBox>

              <MDBox pt={3} pb={3} px={3}>
                <MDBox component="form" role="form" onSubmit={handleSubmit}>
                  <Grid container spacing={1}> 
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        type="text"
                        label="Nombre del Representante Comercial"
                        fullWidth
                        required
                        value={formData.nombre_representante}
                        onChange={handleInputChange('nombre_representante')}
                        onBlur={handleFieldBlur('nombre_representante')}
                        error={!!errores.nombre_representante}
                        helperText={errores.nombre_representante}
                        color={getFieldColor('nombre_representante')}
                        InputProps={{
                          endAdornment: getValidationIcon('nombre_representante'),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        type="text"
                        label="Número de Identificación"
                        fullWidth
                        required
                        value={formData.numero_identificacion}
                        onChange={handleInputChange('numero_identificacion')}
                        onBlur={handleFieldBlur('numero_identificacion')}
                        error={!!errores.numero_identificacion}
                        helperText={errores.numero_identificacion}
                        color={getFieldColor('numero_identificacion')}
                        InputProps={{
                          endAdornment: getValidationIcon('numero_identificacion'),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        type="email"
                        label="Correo Electrónico"
                        fullWidth
                        required
                        value={formData.correo_electronico}
                        onChange={handleInputChange('correo_electronico')}
                        onBlur={handleFieldBlur('correo_electronico')}
                        error={!!errores.correo_electronico}
                        helperText={errores.correo_electronico}
                        color={getFieldColor('correo_electronico')}
                        InputProps={{
                          endAdornment: getValidationIcon('correo_electronico'),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        type="text" // Usar text para permitir cualquier entrada, la validación se encarga de los números
                        label="Teléfono de Contacto"
                        fullWidth
                        required
                        value={formData.telefono_contacto}
                        onChange={handleInputChange('telefono_contacto')}
                        onBlur={handleFieldBlur('telefono_contacto')}
                        error={!!errores.telefono_contacto}
                        helperText={errores.telefono_contacto}
                        color={getFieldColor('telefono_contacto')}
                        InputProps={{
                          endAdornment: getValidationIcon('telefono_contacto'),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        select
                        label="País de Operación"
                        fullWidth
                        required
                        value={formData.region_operacion_pais}
                        onChange={handleInputChange('region_operacion_pais')}
                        onBlur={handleFieldBlur('region_operacion_pais')}
                        error={!!errores.region_operacion_pais}
                        helperText={errores.region_operacion_pais}
                        color={getFieldColor('region_operacion_pais')}
                        sx={{
                          '& .MuiSelect-select': {
                            textAlign: formData.region_operacion_pais ? 'left' : 'center',
                            padding: '16px 14px',
                            minHeight: '48px'
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '56px'
                          }
                        }}
                      >
                        <MenuItem value="">Seleccione País</MenuItem>
                        {LATIN_AMERICAN_COUNTRIES.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </MDInput>
                    </Grid>
                    <Grid item xs={12} md={6}> 
                      <MDInput
                        select
                        label="Ciudad de Operación"
                        fullWidth
                        required
                        value={formData.region_operacion_ciudad}
                        onChange={handleInputChange('region_operacion_ciudad')}
                        onBlur={handleFieldBlur('region_operacion_ciudad')}
                        error={!!errores.region_operacion_ciudad}
                        helperText={errores.region_operacion_ciudad}
                        color={getFieldColor('region_operacion_ciudad')}
                        disabled={!formData.region_operacion_pais}
                        sx={{
                          '& .MuiSelect-select': {
                            textAlign: formData.region_operacion_ciudad ? 'left' : 'center',
                            padding: '16px 14px',
                            minHeight: '48px'
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '56px'
                          }
                        }}
                      >
                        <MenuItem value="">Seleccione Ciudad</MenuItem>
                        {availableCities.map((city) => (
                          <MenuItem key={city} value={city}>
                            {city}
                          </MenuItem>
                        ))}
                      </MDInput>
                    </Grid>
                  </Grid>

                  <MDBox mt={4} mb={1}>
                    <MDButton
                      variant="gradient"
                      color="primary"
                      fullWidth
                      type="submit"
                      disabled={loading}
                      sx={{
                        background: (!loading) ? "#9C27B0" : "#E0E0E0", // Usar el color primario
                        color: (!loading) ? "#FFFFFF" : "#9E9E9E",
                        "&:hover": {
                          background: (!loading) ? "#7B1FA2" : "#E0E0E0",
                          color: (!loading) ? "#FFFFFF" : "#9E9E9E",
                        },
                      }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {loading ? "REGISTRANDO..." : "ENVIAR"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={showValidationAlert}
        autoHideDuration={4000}
        onClose={() => setShowValidationAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 10000 }}
      >
        <Alert
          severity={currentAlert?.type || 'info'}
          onClose={() => setShowValidationAlert(false)}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setShowValidationAlert(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            borderRadius: "8px",
            minWidth: "300px"
          }}
        >
          {currentAlert?.message}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 10000 }}
      >
        <Alert
          severity="error"
          onClose={() => setShowAlert(false)}
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            borderRadius: "8px",
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default GestionMinoristas;